import { atom } from 'jotai'
import * as workspaceApi from '../../services/api/workspace'
import { getUserInfo } from '../../services/api/user'
import {
  workspaces,
  activeWorkspace,
  workspacesLoading,
  workspacesLoaded,
  workspacesError,
  workspaceMembers,
  membersLoading
} from './atoms'

const WORKSPACE_REMOVAL_NOTICE_KEY = 'workspaceRemovalNotice'
const LAST_ACTIVE_WORKSPACE_KEY = 'lastKnownActiveWorkspaceUuid'
const LAST_ACTIVE_WORKSPACE_TYPE_KEY = 'lastKnownActiveWorkspaceType'

const getConfirmedRemovalNotice = async ({ active, previousActiveWorkspaceUuid, workspaceList }) => {
  const wasPreviousWorkspaceRemoved = previousActiveWorkspaceUuid &&
    !workspaceList.find(ws => ws.uuid === previousActiveWorkspaceUuid)
  if (!active || !wasPreviousWorkspaceRemoved) return null
  try {
    const notice = await workspaceApi.getRemovalNotice(previousActiveWorkspaceUuid)
    if (!notice?.removedWorkspaceUuid) return null
    return {
      ...notice,
      fallbackWorkspaceUuid: active.uuid
    }
  } catch (_) {
    return null
  }
}

export const loadWorkspaces = atom(
  (get) => ({ loading: get(workspacesLoading) }),
  async (get, set) => {
    if (get(workspacesLoading)) return
    set(workspacesLoading, true)
    set(workspacesLoaded, false)
    try {
      const list = await workspaceApi.getWorkspaces()
      set(workspaces, list)
      // Check localStorage override first (set during workspace switch)
      const override = localStorage.getItem('activeWorkspaceOverride') // eslint-disable-line no-undef
      let active = null
      if (override) {
        active = list.find(ws => ws.uuid === override)
        localStorage.removeItem('activeWorkspaceOverride') // eslint-disable-line no-undef
        localStorage.removeItem('activeWorkspaceOverrideType') // eslint-disable-line no-undef
      }
      if (!active) {
        // Fall back to server-side active_workspace_uuid
        try {
          const userInfo = await getUserInfo()
          const serverActiveId = userInfo?.activeWorkspaceUuid
          active = serverActiveId && list.find(ws => ws.uuid === serverActiveId)
        } catch (_) {}
      }
      if (!active) {
        active = list.find(ws => ws.type === 'personal') || list[0]
      }
      const previousActiveWorkspaceUuid = localStorage.getItem(LAST_ACTIVE_WORKSPACE_KEY) // eslint-disable-line no-undef
      if (active) {
        const removalNotice = await getConfirmedRemovalNotice({
          active,
          previousActiveWorkspaceUuid,
          workspaceList: list
        })
        if (removalNotice) {
          localStorage.setItem(WORKSPACE_REMOVAL_NOTICE_KEY, JSON.stringify(removalNotice)) // eslint-disable-line no-undef
        }
        localStorage.setItem(LAST_ACTIVE_WORKSPACE_KEY, active.uuid) // eslint-disable-line no-undef
        localStorage.setItem(LAST_ACTIVE_WORKSPACE_TYPE_KEY, active.type || '') // eslint-disable-line no-undef
        set(activeWorkspace, active)
      }
    } catch (error) {
      set(workspacesError, error)
    }
    set(workspacesLoaded, true)
    set(workspacesLoading, false)
  }
)

export const switchWorkspaceAction = atom(
  null,
  async (get, set, workspaceUuid) => {
    await workspaceApi.switchWorkspace({ workspaceUuid })
    const selectedWorkspace = get(workspaces).find(ws => ws.uuid === workspaceUuid)
    // Save selection to localStorage so loadWorkspaces picks it up after reload
    localStorage.setItem('activeWorkspaceOverride', workspaceUuid) // eslint-disable-line no-undef
    if (selectedWorkspace?.type) {
      localStorage.setItem('activeWorkspaceOverrideType', selectedWorkspace.type) // eslint-disable-line no-undef
    } else {
      localStorage.removeItem('activeWorkspaceOverrideType') // eslint-disable-line no-undef
    }
    // Always land on /dashboard — projects view is the same for both workspace types
    window.location.href = '/dashboard' // eslint-disable-line no-undef
  }
)

export const loadMembers = atom(
  (get) => ({ loading: get(membersLoading) }),
  async (get, set, workspaceId) => {
    if (get(membersLoading)) return
    set(membersLoading, true)
    try {
      const data = await workspaceApi.getMembers(workspaceId)
      set(workspaceMembers, data)
    } catch (error) {
      set(workspacesError, error)
    }
    set(membersLoading, false)
  }
)

export const inviteMember = atom(
  null,
  async (get, set, { workspaceId, email, role, message }) => {
    const result = await workspaceApi.inviteUser(workspaceId, { email, role, message })
    const current = get(workspaceMembers)
    set(workspaceMembers, {
      ...current,
      invitations: [...current.invitations, result.invitation]
    })
    return result
  }
)

export const removeMemberAction = atom(
  null,
  async (get, set, { workspaceId, memberId }) => {
    await workspaceApi.removeMember(workspaceId, memberId)
    const current = get(workspaceMembers)
    set(workspaceMembers, {
      ...current,
      members: current.members.filter(m => m.member_uuid !== memberId)
    })
  }
)

export const resendInviteAction = atom(
  null,
  async (get, set, { workspaceId, invitation }) => {
    const result = await workspaceApi.resendInvite(workspaceId, {
      email: invitation.email
    })
    const current = get(workspaceMembers)
    set(workspaceMembers, {
      ...current,
      invitations: current.invitations.map(item => item.uuid === invitation.uuid ? result.invitation : item)
    })
    return result
  }
)

export const cancelInviteAction = atom(
  null,
  async (get, set, { workspaceId, invitationId }) => {
    await workspaceApi.cancelInvite(workspaceId, invitationId)
    const current = get(workspaceMembers)
    set(workspaceMembers, {
      ...current,
      invitations: current.invitations.filter(invitation => invitation.uuid !== invitationId)
    })
  }
)
